import type { MouseEventHandler, ReactNode } from 'react';
import React, {
  useEffect,
  useMemo,
  useState,
  lazy,
  Suspense,
  useCallback,
  forwardRef
} from 'react';
import { DelayInput, Picture } from '@ui';
import { twcls } from '@utils/twcls';
import { PICKUP_MAP_CENTER } from '@constants/geo';
import useCreateRefsFromList from '@customHooks/useCreateRefsFromList';
import icons from '@icons';
import useWindowSize from '@customHooks/useWindowSize';
// ЭТАП 1 · Шаг 3 — ClientOnly: карта Яндекса client-only + apikey пуст на
// сервере → без обёртки structural hydration mismatch.
import { ClientOnly } from '@tanstack/react-router';
import { ShopType, type ShopFragmentMainEndpointFragment } from '@schema/main';
import LoadingPage from '@components/LoadingPage';
import { transformScheduleToStoreInfoSchedule } from '@utils/processSchedule';
import StoreInfoSchedule from '@components/AddressSelection/StoreInfo/components/StoreInfoSchedule';
import { Link } from '@compat/router';
import { observer } from 'mobx-react';
import type { PlacemarkI } from '@components/MapRender';
import { buildShopTypePlacemarkOptions } from '@components/MapRender/placemarks';
import { useShops } from '@customHooks/useShops/useShops';
import './styles.scss';
import { useStores } from '@root/src/stores';

const StoresMap = lazy(async () => await import('@components/MapRender'));

const {
  list: { black: listBlackOutlined },
  bonus: { red: bonusRubleRed }
} = icons.Outline;

const {
  pin: { black: pinBlackOutlined },
  phone: { black: phoneBlackFilled },
  clock: { grey: clockGreyFilled }
} = icons.Fill;

const StoreTypeTitles = {
  cafe: 'Кафе',
  oyster_bar: 'Гастроном | устричный бар',
  shop: 'Гастроном'
};

interface OurAddressesProps {
  shopInfo?: ShopFragmentMainEndpointFragment;
  isOnlyShops?: boolean;
  isOnlyBars?: boolean;
  isBarsLinksAsShops?: boolean;
  onClickMoreInfo?: () => void;
  title?: ReactNode;
}

const initialGeo = PICKUP_MAP_CENTER;

const OurAddresses = observer(
  forwardRef<HTMLElement, OurAddressesProps>(
    (
      {
        isBarsLinksAsShops,
        shopInfo,
        onClickMoreInfo,
        title,
        isOnlyShops = false,
        isOnlyBars = false
      },
      ref
    ) => {
      const { configStore } = useStores();
      const apikey = configStore?.getValue('api_yandex_key');
      const { shops, isShopsLoading } = useShops({ isOnlyBars, isOnlyShops });
      const [input, setInput] = useState('');
      const [mobileTab, setMobileTab] = useState(0);
      const [isInitialMount, setIsInitialMount] = useState(true);
      const [selectedShopId, setSelectedShopId] =
        useState<ShopFragmentMainEndpointFragment['id']>();

      const { isMobile, isTablet } = useWindowSize();

      useEffect(() => {
        if (isShopsLoading) {
          return;
        }
        if (
          !selectedShopId ||
          (shopInfo?.id && shopInfo.id !== selectedShopId)
        ) {
          if (shopInfo?.id && shops?.find(i => i?.id === shopInfo?.id)) {
            setSelectedShopId(shopInfo.id);
          } else if (shops?.[0]?.id) {
            setSelectedShopId(shops[0].id);
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [shopInfo?.id, isShopsLoading]);

      const filteredShops = useMemo(
        () =>
          [...shops]
            .sort(
              (a, b) =>
                Number(b?.type === ShopType.OysterBar) -
                Number(a?.type === ShopType.OysterBar)
            )
            .sort((a, b) => Number(a.opens_soon) - Number(b.opens_soon))
            .filter(
              item =>
                item?.address?.toLowerCase().includes(input.toLowerCase()) ||
                item?.title?.toLowerCase().includes(input.toLowerCase())
            ),
        [input, shops]
      );

      const [shopRefs, itemFunc] = useCreateRefsFromList(filteredShops);

      const onClickMoreInfoHandler: MouseEventHandler = useCallback(
        e => {
          e?.stopPropagation();
          onClickMoreInfo?.();
        },
        [onClickMoreInfo]
      );

      const placemarks = useMemo<PlacemarkI[]>(
        () =>
          filteredShops.map((item, index) => ({
            geometry: item.geo || [],
            onClick: () => {
              shopRefs.current?.[index]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start'
              });
              setSelectedShopId(item.id);
              setIsInitialMount(false);
            },
            options: buildShopTypePlacemarkOptions(
              item.type === ShopType.OysterBar,
              !!item.opens_soon,
              true
            )
          })),
        [filteredShops, shopRefs]
      );

      const mapHeight = useMemo(
        () => (isTablet ? '276px' : '460px'),
        [isTablet]
      );

      const isShowList = useMemo(
        () => (isMobile ? mobileTab === 0 : true),
        [isMobile, mobileTab]
      );

      const isShowMap = useMemo(
        () => (isMobile ? mobileTab === 1 : true),
        [isMobile, mobileTab]
      );

      const selectedShop = useMemo(
        () => shops.find(shop => shop.id === selectedShopId),
        [selectedShopId, shops]
      );

      return (
        <section ref={ref} className='our-addresses' id='addresses'>
          <div className='our-addresses__container'>
            {title ?? (
              <h2 className='our-addresses__title oysterbar-test-title-secondary'>
                Наши адреса
              </h2>
            )}

            <div
              className={twcls(
                'grid grid-cols-[450px,auto] md:grid-cols-[354px,auto] gap-x-[60px] md:gap-x-5 sm:flex sm:flex-col h-[460px] md:h-[276px] sm:h-[410px]'
              )}
            >
              <div className='flex flex-col h-full'>
                <div className='flex flex-col h-full sm:block sm:h-auto allMd:mb-5'>
                  {isMobile && (
                    <div className='mb-4'>
                      <ul className='grid grid-cols-2 rounded-lg bg-[var(--light-grey)] p-0.5'>
                        {[
                          {
                            id: 0,
                            title: 'Список',
                            icon: listBlackOutlined
                          },
                          {
                            id: 1,
                            title: 'На карте',
                            icon: pinBlackOutlined
                          }
                        ].map(item => (
                          <li
                            key={item.id}
                            onClick={() => {
                              setMobileTab(item.id);
                            }}
                            className={twcls(
                              'flex items-center justify-center text-xs py-3 w-full rounded-[7px] font-medium',
                              mobileTab === item.id &&
                                'bg-white text-[var(--main-font-color)]'
                            )}
                          >
                            <Picture
                              pictureClassName='mr-1'
                              icon={item.icon}
                              width='14'
                              height='14'
                            />
                            {item.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className='our-addresses__search'>
                    <DelayInput
                      delay={0}
                      size='medium'
                      changeParams={(_, b) => {
                        setInput(b);
                      }}
                      placeholder='Введите адрес или метро'
                    />
                  </div>

                  {Number(configStore?.data?.cashback_for_pickup) > 0 && (
                    <div className='our-addresses__cashback'>
                      <Picture icon={bonusRubleRed} height='16' />
                      <span>
                        Вернем {configStore?.data?.cashback_for_pickup}%
                        бонусами при самовывозе
                      </span>
                    </div>
                  )}

                  <ul
                    className={twcls(
                      'our-addresses__list hidden flex-col h-[378px] md:h-[198px] overflow-auto sm:h-auto gap-y-3 custom-scroll sm:mt-6 pr-2',
                      isShowList && 'flex'
                    )}
                  >
                    {isShopsLoading ? (
                      <LoadingPage smallMt />
                    ) : (
                      filteredShops.map((item, index) => (
                        <li
                          key={item.title}
                          ref={elementRef => {
                            if (elementRef) {
                              itemFunc(elementRef, index);
                            }
                          }}
                          onClick={() => {
                            setSelectedShopId(item.id);
                            setIsInitialMount(false);
                            if (isMobile) {
                              setMobileTab(1);
                            }
                          }}
                          className={twcls(
                            'w-full px-3 py-3 pb-2 text-sm rounded-xl cursor-pointer hover:bg-[#f5f5f5] transition-colors mb-1',
                            selectedShopId === item.id && 'bg-[#eeeeee]'
                          )}
                        >
                          <div className='w-full'>
                            <div className='flex items-center justify-between mb-1'>
                              <div className='flex items-center gap-2 text-base font-medium'>
                                {item.title}
                                {item.opens_soon && (
                                  <div className='bg-[var(--mid-yellow)] px-2 py-0.5 rounded-md text-[#363636] text-xs font-normal leading-[14px]'>
                                    Скоро
                                  </div>
                                )}
                              </div>
                              <div className='flex items-center gap-2'>
                                <Link
                                  onClick={onClickMoreInfoHandler}
                                  className='hover:underline hover:text-[#6D6D6D] transition-colors flex items-center gap-1'
                                  to={
                                    isBarsLinksAsShops || item.type === 'shop'
                                      ? `/shop-${item.identify}`
                                      : `/ustrichnyj-bar-${item.identify}`
                                  }
                                >
                                  <span className='md:inline sm:hidden text-[#6D6D6D] text-xs font-normal leading-[14px]'>
                                    Подробнее
                                  </span>
                                  <svg
                                    width='12'
                                    height='12'
                                    viewBox='0 0 12 12'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                  >
                                    <path
                                      d='M4.5 9L7.5 6L4.5 3'
                                      stroke='#666'
                                      strokeWidth='1.5'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                  </svg>
                                </Link>
                              </div>
                            </div>

                            {item.type && (
                              <div className='text-[var(--ultra-dark-grey)] mb-2 flex items-center text-xs'>
                                {StoreTypeTitles[item.type]}
                                {(item as any)?.metroStations?.length > 0 && (
                                  <div className='metro-station-block'>
                                    <div
                                      className='metro-circle'
                                      style={{
                                        backgroundColor:
                                          (item as any)?.metroStations?.[0]
                                            ?.line?.color || '#ccc'
                                      }}
                                    />
                                    <span
                                      className='metro-station-text'
                                      style={{
                                        color:
                                          (item as any)?.metroStations?.[0]
                                            ?.line?.color || '#666'
                                      }}
                                    >
                                      {(item as any)?.metroStations?.[0]?.title}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className='mb-2 text-[#363636] text-sm font-normal leading-[18px]'>
                              {item.address}
                            </div>

                            <div className='flex items-center gap-3 text-[var(--ultra-dark-grey)]'>
                              <div className='flex items-center gap-1'>
                                <Picture
                                  icon={phoneBlackFilled}
                                  width='12'
                                  height='12'
                                  alt='Телефон'
                                />
                                <a
                                  href={`tel:${item.phone}`}
                                  className='hover:text-[var(--dark-red)] transition-colors text-sm'
                                  onClick={e => {
                                    e.stopPropagation();
                                  }}
                                >
                                  {item.phone}
                                </a>
                              </div>
                              {item.schedule && (
                                <div className='flex items-center gap-1'>
                                  <Picture
                                    icon={clockGreyFilled}
                                    width='12'
                                    height='12'
                                    alt='Время работы'
                                  />
                                  <StoreInfoSchedule
                                    list={transformScheduleToStoreInfoSchedule(
                                      item.schedule
                                    )}
                                    className='text-[var(--ultra-dark-grey)] text-xs'
                                    showTimeOnly={true}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </div>

              <div
                className={twcls(
                  'hidden rounded-2.5xl overflow-hidden',
                  isShowMap && 'block'
                )}
              >
                <div className='flex flex-col w-full h-full'>
                  <div className='h-full'>
                    <ClientOnly fallback={null}>
                    {apikey && (
                      <Suspense fallback={<LoadingPage smallMt />}>
                        <StoresMap
                          height={mapHeight}
                          center={
                            isInitialMount
                              ? shopInfo?.geo || initialGeo
                              : selectedShop?.geo || initialGeo
                          }
                          zoom={isInitialMount ? (shopInfo?.geo ? 14 : 10) : 16}
                          placemark={placemarks}
                        />
                      </Suspense>
                    )}
                    </ClientOnly>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    }
  )
);

export default OurAddresses;
